<script setup>
import { computed } from 'vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const headerComponent = computed(() => {
  return props.template.components?.find(c => c.type === 'HEADER');
});

const bodyComponent = computed(() => {
  return props.template.components?.find(c => c.type === 'BODY');
});

const footerComponent = computed(() => {
  return props.template.components?.find(c => c.type === 'FOOTER');
});

const buttonComponent = computed(() => {
  return props.template.components?.find(c => c.type === 'BUTTONS');
});

const headerType = computed(() => {
  if (!headerComponent.value) return null;
  return headerComponent.value.format || 'TEXT';
});

const headerText = computed(() => {
  return headerComponent.value?.text || '';
});

const headerMediaUrl = computed(() => {
  const example = headerComponent.value?.example;
  if (example?.header_handle && example.header_handle[0]) {
    return example.header_handle[0];
  }
  if (example?.header_url && example.header_url[0]) {
    return example.header_url[0];
  }
  return '';
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const footerText = computed(() => {
  return footerComponent.value?.text || '';
});

const buttons = computed(() => {
  return buttonComponent.value?.buttons || [];
});

function formatWhatsAppText(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Monospace: ```text```
  html = html.replace(
    /```([^`]+)```/g,
    '<code class="font-mono text-[12px] bg-black/5 dark:bg-white/10 rounded px-1 py-0.5">$1</code>'
  );
  // Bold: *text*
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
  // Italic: _text_
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  // Strikethrough: ~text~
  html = html.replace(/~(.*?)~/g, '<del>$1</del>');
  // Variable styling: {{var_name}} or {{1}}
  html = html.replace(
    /\{\{([^}]+)\}\}/g,
    '<span class="inline-block px-1 py-0.2 rounded bg-n-brand/15 text-n-brand dark:bg-n-brand/25 font-semibold text-[12px]">{{$1}}</span>'
  );
  return html;
}

const formattedBody = computed(() => {
  return formatWhatsAppText(bodyText.value);
});

const currentTime = computed(() => {
  return new Date()
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
});
</script>

<template>
  <div
    class="relative w-full rounded-2xl bg-[#efeae2] dark:bg-[#0b141a] p-4 sm:p-6 flex flex-col items-start overflow-hidden border border-n-weak"
  >
    <!-- Wallpaper overlay pattern -->
    <!-- eslint-disable-next-line vue/no-static-inline-styles -->
    <div
      class="absolute inset-0 opacity-[0.06] dark:opacity-[0.03] pointer-events-none"
      style="
        background-image: url('https://static.whatsapp.net/rsrc.php/v3/yl/r/r_QZ3OA1N_M.png');
        background-repeat: repeat;
      "
    />

    <!-- WhatsApp message bubble -->
    <div
      class="relative z-10 w-full max-w-[320px] rounded-2xl rounded-tl-sm bg-[#e7fed6] dark:bg-[#005c4b] p-3 shadow-md flex flex-col gap-1.5"
    >
      <!-- Header -->
      <div v-if="headerComponent" class="mb-1">
        <p
          v-if="headerType === 'TEXT'"
          class="text-sm font-bold text-[#111b21] dark:text-[#e9edef]"
        >
          {{ headerText }}
        </p>
        <div
          v-else-if="headerType === 'IMAGE'"
          class="w-full rounded-xl overflow-hidden bg-black/5"
        >
          <img
            v-if="headerMediaUrl"
            :src="headerMediaUrl"
            class="w-full h-auto object-cover max-h-48"
            alt="Header Image"
          />
          <div
            v-else
            class="w-full h-32 rounded-xl bg-black/10 dark:bg-white/10 flex flex-col items-center justify-center gap-1 text-n-slate-11"
          >
            <span class="i-lucide-image text-3xl" />
            <span class="text-xs font-medium">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.TYPE.IMAGE') }}
            </span>
          </div>
        </div>
        <div
          v-else-if="headerType === 'VIDEO'"
          class="w-full rounded-xl overflow-hidden bg-black"
        >
          <video
            v-if="headerMediaUrl"
            :src="headerMediaUrl"
            controls
            class="w-full h-auto max-h-48"
          />
          <div
            v-else
            class="w-full h-32 rounded-xl bg-black/10 dark:bg-white/10 flex flex-col items-center justify-center gap-1 text-n-slate-11"
          >
            <span class="i-lucide-video text-3xl" />
            <span class="text-xs font-medium">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.TYPE.VIDEO') }}
            </span>
          </div>
        </div>
        <div
          v-else-if="headerType === 'DOCUMENT'"
          class="w-full h-14 rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 flex items-center px-3 gap-3"
        >
          <span
            class="i-lucide-file-text text-2xl text-[#667781] dark:text-[#8696a0]"
          />
          <span
            class="text-xs font-medium text-[#111b21] dark:text-[#e9edef] truncate"
          >
            {{ headerMediaUrl.split('/').pop() || $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.TYPE.DOCUMENT') }}
          </span>
        </div>
        <div
          v-else-if="headerType === 'LOCATION'"
          class="w-full h-28 rounded-xl bg-[#e8e9eb] dark:bg-[#111b21] flex flex-col items-center justify-center gap-1"
        >
          <span class="i-lucide-map-pin text-3xl text-n-ruby-10" />
          <span class="text-xs font-semibold text-[#111b21] dark:text-[#e9edef]">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER.TYPE.LOCATION') }}
          </span>
        </div>
      </div>

      <!-- Body text -->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p
        class="text-[13px] text-[#111b21] dark:text-[#e9edef] whitespace-pre-wrap break-words leading-relaxed"
        v-html="formattedBody"
      />

      <!-- Footer & timestamp -->
      <div class="flex items-center justify-between gap-2 mt-1">
        <p
          v-if="footerText"
          class="text-[11px] text-[#667781] dark:text-[#8696a0] truncate"
        >
          {{ footerText }}
        </p>
        <div class="flex items-center gap-1 ml-auto flex-shrink-0">
          <span class="text-[11px] text-[#667781] dark:text-[#8696a0]">
            {{ currentTime }}
          </span>
          <span class="i-lucide-check-check text-[13px] text-[#53bdeb]" />
        </div>
      </div>
    </div>

    <!-- Buttons preview -->
    <div
      v-if="buttons.length > 0"
      class="relative z-10 w-full max-w-[320px] flex flex-col gap-1.5 mt-2"
    >
      <div
        v-for="(button, index) in buttons"
        :key="index"
        class="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-white dark:bg-[#1f2c33] text-[13px] font-medium text-[#00a5f4] shadow-sm border border-black/5 dark:border-white/5 hover:bg-black/5 transition-colors cursor-pointer select-none"
      >
        <span
          v-if="button.type === 'URL'"
          class="i-lucide-external-link text-xs"
        />
        <span
          v-else-if="button.type === 'PHONE_NUMBER'"
          class="i-lucide-phone text-xs"
        />
        <span
          v-else-if="button.type === 'COPY_CODE'"
          class="i-lucide-copy text-xs"
        />
        <span v-else class="i-lucide-corner-down-left text-xs" />
        <span class="truncate">
          {{ button.text || $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS.TYPE.LABEL') }}
        </span>
      </div>
    </div>
  </div>
</template>
